import {
  EditOutlined,
  PercentageOutlined,
  PlusCircleOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import {
  ATable,
  DropdownOptions,
  Loading,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
  StatusActive,
} from '../../components';
import { useToast } from '../../hooks/toast';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import TaxeCreateEdit from './CreateEdit';
import { ITaxe } from '../../shared/interfaces';
import { currency } from '../../shared/utils/functions';
import { getTaxes } from '../../http';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const Taxes: React.FC = () => {
  const { payment_method_id } = useParams();
  const { addToast } = useToast();
  const { permissionFind } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [paymentMethodDescription, setPaymentMethodDescription] =
    useState<string>('');
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [taxes, setTaxes] = useState<ITaxe[]>([]);
  const [taxe, setTaxe] = useState<ITaxe | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const list = useCallback(async () => {
    setLoading(true);
    setTaxes([]);
    try {
      const response = await getTaxes(
        limit,
        payment_method_id,
        (page - 1) * limit,
        search,
      );
      setPages(response.count);
      setTaxes(response.data);
      setPaymentMethodDescription(response.payment_method_description);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar planos de contas',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  const renderModalTaxeCreateEdit = () => {
    if (modalCreate)
      return (
        <TaxeCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          taxe={taxe}
          setTaxes={setTaxes}
          payment_method_id={payment_method_id}
        />
      );
  };

  useEffect(() => {
    list();
  }, [page, search, limit, list]);

  if (loading) return <Loading load />;

  return (
    <PageLayout>
      <PageIntro
        badge="Financeiro"
        icon={<PercentageOutlined />}
        title={`Taxas: ${paymentMethodDescription}`}
        description="Gerencie taxas por forma de pagamento com o mesmo padrão visual das demais listagens administrativas."
      />
      <MenuOptions
        showButtonBack
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => list(),
          },
          permissionFind([PermissionActions.taxesCreate])
            ? {
                label: 'Cadastrar',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => {
                  setTaxe(null);
                  setModalCreate(true);
                },
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <ATable
          columns={[
            {
              title: 'Id',
              width: 100,
              render: (value, record, index) => <>{index + 1}</>,
            },
            {
              title: 'Descrição',
              dataIndex: 'description',
            },
            {
              title: 'Taxa',
              render: (cell: ITaxe) => (
                <>{cell.type ? currency(cell.value) : `${cell.value ?? 0}%`} </>
              ),
            },
            {
              title: 'Parcela',
              render: (cell: ITaxe) => (
                <>
                  Parcelado {cell.initial_installment}-{cell.final_installment}x
                </>
              ),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: 'Opções',
              width: 130,
              render: (cell: ITaxe) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.taxesUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setTaxe(cell);
                            setModalCreate(true);
                          },
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={taxes}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {renderModalTaxeCreateEdit()}
    </PageLayout>
  );
};

export default Taxes;
